// Pure logic for the post-auth profile steps (Name → Country → Username) that
// bring the web signup flow in line with the mobile apps.
//
// On iOS and Android a brand-new account walks a short wizard right after
// authenticating: Name, Country, Username (see OnBoardingParentFragment on
// Android and the OnBoardingV2 storyboard on iOS). The web had no equivalent —
// accounts were created with a blank `display_name` and no country, and the
// nightly `users:assign_display_names` task later stamped a random name on them.
//
// Everything here is dependency-free and deterministic so the gating rules and
// the username suggestions can be unit-tested without a store, a network, or a
// DOM. The Vue components own presentation only.

import { OnBoardingGoal } from '../models/OnBoardingGoal';

/** Rails' `blank?` — null, empty, or whitespace-only all count as absent. */
export const isBlank = (value?: string | null): boolean => !value || value.trim().length === 0;

export interface SignupProfileUser {
  displayName?: string | null;
  title?: string | null;
  country?: { id?: string | number | null } | null;
}

export interface ProfileSetupContext {
  /** Firebase's isNewUser, mirrored onto window.isNewUser by the auth actions. */
  isNewUser?: boolean | null;
  /** The OnBoardingGoal the flow was launched for. */
  goal?: string | null;
  /** True on the standalone /login page, false inside the modal. */
  standaloneMode?: boolean | null;
  /** The Rails user as last read by GetUser. */
  serverUser?: SignupProfileUser | null;
}

/**
 * Goals that represent a *deliberate* signup — the user set out to make an
 * account (navbar Sign up, a purchase, a gift card, the DE offer).
 *
 * Every other goal is an interruption: the user clicked "like", "add to list",
 * "map styles" or "redeem" and hit an auth wall on the way to something else.
 * Putting three profile questions in front of that costs conversion for no
 * benefit, so those flows authenticate and go straight back to what the user
 * was doing. See shouldRunProfileSetup.
 */
export const DELIBERATE_SIGNUP_GOALS: ReadonlyArray<string> = Object.freeze([
  OnBoardingGoal.CreateAccount,
  OnBoardingGoal.CreateAccountForPurchase,
  OnBoardingGoal.CreateAccountForGermany,
  OnBoardingGoal.CreateAccountForGiftCard,
]);

export const isDeliberateSignupGoal = (goal?: string | null): boolean =>
  !!goal && DELIBERATE_SIGNUP_GOALS.includes(goal);

/**
 * True when the Rails user already has both things the wizard collects.
 *
 * Used to skip the wizard for an account that somehow already has a profile
 * (a re-run, or an account created by another client), so we never ask twice.
 * `title` (full name) is deliberately not part of this: it is prefilled rather
 * than required, exactly as on mobile.
 */
export const profileIsComplete = (user?: SignupProfileUser | null): boolean =>
  !!user && !isBlank(user.displayName) && user.country?.id != null;

/**
 * The network-free half of the gate: is this the kind of sign-in that could
 * warrant the wizard at all?
 *
 * Split out so the caller can answer it *before* spending a GetUser round-trip.
 * The overwhelming majority of sign-ins (existing users, and every interruption
 * flow) fail here and pay nothing.
 */
export const profileSetupMayApply = (
  context: Pick<ProfileSetupContext, 'isNewUser' | 'goal' | 'standaloneMode'>,
): boolean => {
  const { isNewUser, goal, standaloneMode } = context;
  if (!isNewUser) return false;
  return !!standaloneMode || isDeliberateSignupGoal(goal);
};

/**
 * The gate for the whole wizard.
 *
 * Three independent conditions, all required:
 *  - a brand-new account (an existing user signing in is never interrupted),
 *  - a deliberate signup, or the standalone /login page,
 *  - a profile that is actually missing something.
 *
 * A null/unknown server user returns false: if we could not read the record we
 * do not know what is there, and asking is better handled on the next sign-in
 * than risking a wizard over a complete profile.
 */
export const shouldRunProfileSetup = (context: ProfileSetupContext): boolean => {
  if (!profileSetupMayApply(context)) return false;
  if (!context.serverUser) return false;
  return !profileIsComplete(context.serverUser);
};

// --- Name -----------------------------------------------------------------

/**
 * What to prefill the Name fields with: whatever Rails already has, else the
 * name the identity provider handed us (Google/Apple/Facebook all supply one).
 */
export const initialFullName = (
  serverUser?: SignupProfileUser | null,
  providerName?: string | null,
): string => {
  if (!isBlank(serverUser?.title)) return serverUser!.title!.trim();
  if (!isBlank(providerName)) return providerName!.trim();
  return '';
};

export interface NameParts {
  firstName: string;
  lastName: string;
}

/**
 * Split a single name string into the first/last pair the step asks for.
 *
 * The mobile apps collect two fields and join them (iOS composes
 * `"\(firstName) \(lastName)"`), but everything we can prefill from — Rails'
 * `title`, and the provider's `displayName` — is one string. Everything after
 * the first token is the last name, so "Jane van der Berg" keeps its surname
 * intact rather than losing the particles.
 */
export const splitFullName = (fullName?: string | null): NameParts => {
  const tokens = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { firstName: '', lastName: '' };
  return { firstName: tokens[0], lastName: tokens.slice(1).join(' ') };
};

/** The inverse: what gets written to `users.title`. */
export const composeFullName = (firstName?: string | null, lastName?: string | null): string =>
  [firstName, lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ');

/**
 * Mobile requires both halves ("Please enter your first and last name"), so web
 * does too — otherwise the same account looks differently-filled depending on
 * which client created it.
 */
export const isValidNameParts = (parts: NameParts): boolean =>
  !isBlank(parts?.firstName) && !isBlank(parts?.lastName);

export const isValidFullName = (value?: string | null): boolean => !isBlank(value);

// --- Username -------------------------------------------------------------

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

/**
 * Characters the username field accepts: any letter or digit in any script,
 * plus the separators already common in the DB ("joey-hikes", "SummitRidge42").
 *
 * Unicode-aware on purpose. An ASCII-only rule silently mangled real names —
 * "José" became "Jos", "Ωmega" became "mega", and a name in a non-Latin script
 * was emptied entirely, leaving the user stuck on a step with no skip. The
 * column has no charset restriction and the mobile clients impose none, so a
 * web-only ASCII rule was ours alone to inflict.
 */
const USERNAME_ALLOWED = /[^\p{L}\p{N}._-]/gu;

/**
 * Strip whitespace and anything outside the allowed set, and collapse runs of
 * separators. Applied as the user types so the field can't hold a value the
 * validator would then reject.
 */
export const normalizeUsername = (raw?: string | null): string => {
  if (!raw) return '';
  return raw
    .trim()
    .replace(/\s+/g, '-')
    .replace(USERNAME_ALLOWED, '')
    .replace(/([._-])\1+/g, '$1')
    .slice(0, USERNAME_MAX_LENGTH);
};

export type UsernameError = 'blank' | 'too_short' | 'too_long' | null;

/**
 * Returns an error *code* rather than a message — the component maps it to an
 * i18n string, so this stays pure and locale-free.
 *
 * Note there is no uniqueness check: `users.display_name` has no unique index
 * and no uniqueness validation (duplicates exist in production), so claiming
 * a name is "taken" would be a promise the backend does not keep.
 */
export const validateUsername = (raw?: string | null): UsernameError => {
  const value = normalizeUsername(raw);
  if (isBlank(value)) return 'blank';
  if (value.length < USERNAME_MIN_LENGTH) return 'too_short';
  if (value.length > USERNAME_MAX_LENGTH) return 'too_long';
  return null;
};

/**
 * The hiking vocabulary the iOS app feeds its suggestion prompt
 * (OnBoardingUsernameViewModel.fetchAiSuggestions). Reused here so web
 * suggestions read like the ones a mobile user would have seen.
 */
export const HIKING_WORDS: ReadonlyArray<string> = Object.freeze([
  'Trail', 'Summit', 'Trek', 'Wild', 'Boots', 'Compass', 'Scenic', 'Ascent',
  'Adventure', 'Path', 'Forest', 'Climb', 'Ridge', 'Expedition', 'Terrain',
  'Panorama', 'Wander', 'Roam', 'Peak', 'Basecamp',
]);

/** First word of a name, or the local-part of an email — whichever we have. */
export const nameSeed = (fullName?: string | null, email?: string | null): string => {
  const firstWord = fullName?.trim().split(/\s+/)[0];
  if (firstWord) return normalizeUsername(firstWord);
  const localPart = email?.trim().split('@')[0];
  return normalizeUsername(localPart);
};

export interface SuggestUsernamesOptions {
  count?: number;
  /** Injectable for deterministic tests; defaults to Math.random. */
  random?: () => number;
}

/**
 * Deterministic, offline username suggestions.
 *
 * iOS asks OpenAI for these, with the API key embedded in the app binary. That
 * is not portable to the web — a key shipped to the browser is a public key —
 * so web composes them locally from the same word list instead. No network, no
 * key, no latency, and the shapes match what mobile produces
 * ("JaneTrail", "SummitJane", "JaneRidge42").
 */
export const suggestUsernames = (
  fullName?: string | null,
  email?: string | null,
  options: SuggestUsernamesOptions = {},
): string[] => {
  const { count = 3, random = Math.random } = options;
  const seed = nameSeed(fullName, email);
  if (!seed) return [];

  const words: string[] = [];
  const pool = [...HIKING_WORDS];
  while (words.length < count && pool.length > 0) {
    const index = Math.min(pool.length - 1, Math.floor(random() * pool.length));
    words.push(pool.splice(index, 1)[0]);
  }

  const shapes = [
    (word: string) => `${seed}${word}`,
    (word: string) => `${word}${seed}`,
    (word: string) => `${seed}${word}${Math.floor(random() * 90) + 10}`,
  ];

  const suggestions: string[] = [];
  words.forEach((word, index) => {
    const candidate = normalizeUsername(shapes[index % shapes.length](word));
    if (candidate && !suggestions.includes(candidate)) suggestions.push(candidate);
  });
  return suggestions;
};

// --- Country --------------------------------------------------------------

export interface SignupCountry {
  id: string | number;
  name: string;
  code3l?: string | null;
  code2l?: string | null;
  /** Parent row for a sub-region — a US state, a Canadian province, Isle of Man. */
  countryId?: string | number | null;
}

/**
 * Case-insensitive search over the country list, prefix matches first so typing
 * "ire" puts Ireland above "Côte d'Ivoire". Blank query returns the list as-is.
 */
export const filterCountries = (
  countries: ReadonlyArray<SignupCountry>,
  query?: string | null,
): SignupCountry[] => {
  const list = (countries || []).filter((country) => !isBlank(country?.name));
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return [...list];

  const prefix: SignupCountry[] = [];
  const contains: SignupCountry[] = [];
  list.forEach((country) => {
    const name = country.name.toLowerCase();
    if (name.startsWith(needle)) prefix.push(country);
    else if (name.includes(needle)) contains.push(country);
  });
  return [...prefix, ...contains];
};

/**
 * De-duplicates the country list for display.
 *
 * `countries` holds states as well as countries and has genuine duplicate rows
 * — two "Alaska" (both AK), two "Belgium" (both BEL) — so the raw list shows
 * the same entry twice.
 *
 * The key is name + code3l, not name alone. Some names belong to two genuinely
 * different places: "Georgia" is both the country (GEO) and the US state (GA),
 * and collapsing those would silently drop one and hand the user the wrong id.
 * The parent `country_id` can't be used to tell them apart either — in this
 * data both Georgias carry the same parent.
 */
export const dedupeCountries = (
  countries: ReadonlyArray<SignupCountry>,
): SignupCountry[] => {
  const seen = new Set<string>();
  const result: SignupCountry[] = [];
  (countries || []).forEach((country) => {
    if (isBlank(country?.name)) return;
    const name = country.name.trim().toLowerCase();
    const code = (country.code3l || '').trim().toLowerCase();
    const key = `${name}|${code}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push(country);
  });
  return result;
};

// --- App settings -----------------------------------------------------------

export interface PreferenceOption {
  value: string;
  label: string;
}

export interface PreferenceOptions {
  measurement: PreferenceOption[];
  gridReference: PreferenceOption[];
  hikeTime: PreferenceOption[];
}

export const EMPTY_PREFERENCE_OPTIONS: PreferenceOptions = Object.freeze({
  measurement: [],
  gridReference: [],
  hikeTime: [],
}) as PreferenceOptions;

/**
 * Normalises the option lists Rails publishes as `window.hiikerPreferenceOptions`
 * into `{ value, label }` pairs.
 *
 * Rails is the single source of truth here (Preferenceable), exactly as it is
 * for the language list — a second copy in JS is how `en`/`de` drifted before.
 * That means this has to tolerate the list being absent: an old cached layout,
 * a view that doesn't render the partial, or a unit test. Anything malformed
 * degrades to empty rather than throwing, and the settings step treats empty as
 * "nothing to offer" and lets the user straight through. Every one of these
 * preferences has a server-side default, so skipping costs the user nothing.
 */
export const parsePreferenceOptions = (raw: unknown): PreferenceOptions => {
  const group = (value: unknown): PreferenceOption[] => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    return Object.entries(value as Record<string, unknown>)
      .filter(([key, label]) => !isBlank(key) && typeof label === 'string' && !isBlank(label))
      .map(([key, label]) => ({ value: key, label: label as string }));
  };

  if (!raw || typeof raw !== 'object') return { ...EMPTY_PREFERENCE_OPTIONS };
  const source = raw as Record<string, unknown>;
  return {
    measurement: group(source.measurement),
    gridReference: group(source.gridReference),
    hikeTime: group(source.hikeTime),
  };
};

/**
 * Only send a preference the server will accept. An unknown value would fail
 * the model's inclusion validation and take the whole updateUser call down with
 * it, losing the name and username collected in the earlier steps.
 */
export const isAllowedPreference = (
  options: ReadonlyArray<PreferenceOption>,
  value?: string | null,
): boolean => !isBlank(value) && (options || []).some((option) => option.value === value);

/**
 * The settings the wizard will actually write, dropping anything blank or not
 * offered. Returns an object ready to merge into the updateUser payload.
 */
export interface SettingsDraft {
  measurementPreference?: string | null;
  gridReference?: string | null;
  hikeTime?: string | null;
  locale?: string | null;
}

export const settingsPayload = (
  draft: SettingsDraft,
  options: PreferenceOptions,
  localeCodes: ReadonlyArray<string>,
): Record<string, string> => {
  const payload: Record<string, string> = {};
  if (isAllowedPreference(options.measurement, draft.measurementPreference)) {
    payload.measurementPreference = draft.measurementPreference!;
  }
  if (isAllowedPreference(options.gridReference, draft.gridReference)) {
    payload.gridReference = draft.gridReference!;
  }
  if (isAllowedPreference(options.hikeTime, draft.hikeTime)) {
    payload.hikeTime = draft.hikeTime!;
  }
  if (!isBlank(draft.locale) && (localeCodes || []).includes(draft.locale!)) {
    payload.locale = draft.locale!;
  }
  return payload;
};

/**
 * Region code from a BCP-47 locale tag: "en-GB" → "GB", "zh-Hans-CN" → "CN".
 *
 * Skips script subtags (four letters) and ignores a bare language tag, which
 * carries no region at all.
 */
export const regionFromLocale = (locale?: string | null): string => {
  const subtags = (locale || '').split('-').slice(1);
  const region = subtags.find((tag) => /^[A-Za-z]{2}$/.test(tag));
  return region ? region.toUpperCase() : '';
};

export interface RegionDefaults {
  measurementPreference: string;
  gridReference: string;
}

/**
 * The units and coordinate format to offer for a region, mirroring the rules
 * the mobile apps apply (MeasurementPref.defaultForLocale and
 * GridRef.defaultForLocale on Android).
 *
 * Mobile derives the region from the device locale. Web prefers the country the
 * user just chose on the previous step — explicit input beats a browser setting,
 * and an Irish user on an en-US browser should not be handed Miles/Feet — and
 * only falls back to the browser's region when no country was picked.
 *
 * `UK` is accepted alongside `GB` because Android's rule does; it is not a real
 * ISO region code but has appeared in stored values.
 */
export const defaultSettingsForRegion = (region?: string | null): RegionDefaults => {
  const code = (region || '').trim().toUpperCase();
  const imperial = code === 'US' || code === 'GB' || code === 'UK';

  let gridReference = 'LatLon';
  if (code === 'GB' || code === 'UK') gridReference = 'GbGrid';
  else if (code === 'IE') gridReference = 'IeGrid';
  else if (code === 'NZ') gridReference = 'NzGrid';

  return {
    measurementPreference: imperial ? 'MilesFeet' : 'KilometersMeters',
    gridReference,
  };
};

/**
 * The row whose `code2l` actually describes the region.
 *
 * The countries table holds sub-regions as well as countries, and a sub-region
 * stores its *own* two-letter code there: Texas is "TX", not "US". Reading that
 * directly would hand a user who picks Texas the metric defaults, since "TX"
 * matches none of the imperial regions. The parent row carries the real one, so
 * a sub-region defers to it — which is also right for Isle of Man ("IM"),
 * whose parent GB gives it the GB grid.
 */
export const effectiveRegionCountry = (
  country?: SignupCountry | null,
  countries?: ReadonlyArray<SignupCountry>,
): SignupCountry | null => {
  if (!country) return null;
  if (country.countryId == null) return country;
  const parent = (countries || []).find((entry) => String(entry.id) === String(country.countryId));
  return parent || country;
};

/**
 * The region to base those defaults on: the chosen country's two-letter code
 * (which is the ISO region code), else the browser's locale.
 */
export const signupRegion = (
  country?: SignupCountry | null,
  browserLocale?: string | null,
): string => {
  const fromCountry = (country?.code2l || '').trim();
  if (fromCountry) return fromCountry.toUpperCase();
  return regionFromLocale(browserLocale);
};

// --- "How did you hear about HiiKER?" ---------------------------------------

export interface AttributionSource {
  /** Stored verbatim in `users.attribution_tag`. */
  value: string;
  /** i18n key for what the user actually reads. */
  labelKey: string;
}

/** The nine iOS offers (EmailViewModel.tabs). Kept as its own list so the
 *  overlap between platforms stays visible and testable. */
export const IOS_ATTRIBUTION_VALUES: ReadonlyArray<string> = Object.freeze([
  'Search Engine', 'Facebook Ad', 'Instagram Ad', 'Social media',
  'Word of mouth', 'Podcast', 'YouTube', 'Influencer', 'Other',
]);

/**
 * What the signup step offers: iOS's nine, plus the AI assistants people now
 * arrive from. Those three are web-only for the moment — iOS won't record them
 * and Android doesn't ask the question at all — so a cross-platform total will
 * under-count them until the apps catch up.
 *
 * The AI options sit next to Search engine because that is the same kind of
 * answer ("I looked it up and it told me"); "Other" stays last so the catch-all
 * reads as a catch-all.
 *
 * `value` is the English string and is what gets stored, regardless of the
 * language the page is being read in. `attribution_tag` is a free-text column
 * with nothing constraining it, and iOS sends its *localised* label — so a
 * German user there files "Mundpropaganda" while an English one files "Word of
 * mouth", and the two never group together. Sending a stable value keeps web's
 * answers countable; the label is translated for display only.
 */
export const ATTRIBUTION_SOURCES: ReadonlyArray<AttributionSource> = Object.freeze([
  { value: 'Search Engine', labelKey: 'search_engine' },
  { value: 'ChatGPT', labelKey: 'chatgpt' },
  { value: 'Grok', labelKey: 'grok' },
  { value: 'Muse', labelKey: 'muse' },
  { value: 'Facebook Ad', labelKey: 'facebook_ad' },
  { value: 'Instagram Ad', labelKey: 'instagram_ad' },
  { value: 'Social media', labelKey: 'social_media' },
  { value: 'Word of mouth', labelKey: 'word_of_mouth' },
  { value: 'Podcast', labelKey: 'podcast' },
  { value: 'YouTube', labelKey: 'youtube' },
  { value: 'Influencer', labelKey: 'influencer' },
  { value: 'Other', labelKey: 'other' },
]);

/** Only send a value from the list; anything else would pollute the column. */
export const isAttributionSource = (value?: string | null): boolean =>
  !!value && ATTRIBUTION_SOURCES.some((source) => source.value === value);
